import React from "react";
import {
  Users, RefreshCw, Check, Loader2, Lock, Edit3, Eye, User, Calendar, Film, History, BarChart3, KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Header = ({ onSync, syncing, onOpenTeam, saveStatus, editMode, onEnableEdit, onDisableEdit, onOpenMyJobs, pendingCount, onSaveNow, onOpenHoliday, pendingHolidays, onOpenEditingQueue, onOpenRecentJobs, onOpenTeamStats, onOpenApiAccess }) => {
  return (
    <header className="bg-white border-b border-[#e5e7eb] sticky top-0 z-20">
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <h1
              className="font-heading text-lg sm:text-2xl text-[#605e5e] whitespace-nowrap cursor-pointer select-none"
              onClick={() => {
                if (window.confirm("Log out of the rota?")) {
                  localStorage.removeItem("rota_view_token");
                  sessionStorage.removeItem("rota_view_token");
                  window.location.reload();
                }
              }}
              data-testid="header-title-logout"
            >
              WVE Team Rota
            </h1>
            {editMode ? (
              <Badge className="bg-[#8DA399] text-white font-mono text-[10px] sm:text-xs hidden sm:flex">
                <Edit3 className="h-3 w-3 mr-1" />
                EDIT MODE
              </Badge>
            ) : (
              <Badge variant="outline" className="font-mono text-[10px] sm:text-xs text-[#8d8b8b] hidden sm:flex">
                <Eye className="h-3 w-3 mr-1" />
                VIEW ONLY
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-3">
            {editMode && (
              <div className="hidden sm:flex items-center gap-2 text-sm text-[#8d8b8b] mr-4">
                {saveStatus === 'saving' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="saving-indicator">Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <Check className="h-4 w-4 text-green-600" />
                    <span>Saved</span>
                  </>
                )}
                {saveStatus === 'pending' && pendingCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onSaveNow}
                    className="text-xs h-7 border-amber-400 text-amber-600 hover:bg-amber-50"
                    data-testid="save-now-btn"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Save {pendingCount}
                  </Button>
                )}
              </div>
            )}
            
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenTeam}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="manage-team-btn"
                >
                  <Users className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Team</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSync}
                  disabled={syncing}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="sync-ics-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">Sync</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenRecentJobs}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="recent-jobs-btn-edit"
                >
                  <History className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Recent Jobs</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenEditingQueue}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="editing-queue-btn-edit"
                >
                  <Film className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Editing Que</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenTeamStats}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="team-stats-btn"
                >
                  <BarChart3 className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Stats</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenHoliday}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3 relative"
                  data-testid="holiday-btn-edit"
                >
                  <Calendar className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Holidays</span>
                  {pendingHolidays > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-4 w-4 text-[9px] font-bold bg-amber-500 text-white rounded-full flex items-center justify-center">
                      {pendingHolidays}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenApiAccess}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="api-access-btn"
                >
                  <KeyRound className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">API Access</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDisableEdit}
                  className="text-[#8d8b8b] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="disable-edit-btn"
                >
                  <Eye className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">View Only</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSync}
                  disabled={syncing}
                  className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="sync-calendar-view-btn"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">Sync</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenMyJobs}
                  className="border-[#605e5e] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="my-jobs-btn"
                >
                  <User className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">My Jobs</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenRecentJobs}
                  className="border-[#605e5e] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="recent-jobs-btn"
                >
                  <History className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Recent Jobs</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenEditingQueue}
                  className="border-[#605e5e] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="editing-queue-btn"
                >
                  <Film className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Editing Que</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenHoliday}
                  className="border-[#605e5e] text-[#605e5e] hover:bg-gray-50 h-8 px-2 sm:px-3"
                  data-testid="holiday-btn"
                >
                  <Calendar className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Request Holiday</span>
                </Button>
                <Button
                  size="sm"
                  onClick={onEnableEdit}
                  className="bg-[#605e5e] hover:bg-[#4b4a4a] text-white h-8 px-2 sm:px-3"
                  data-testid="enable-edit-btn"
                >
                  <Lock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Edit Mode</span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
